import type { PageResponse } from "../pagination.ts";
export type { PageResponse };

/** Maximum number of insights returned in a single list request (prefetch limit on the frontend). */
export const INSIGHT_LIST_MAX_LIMIT = 200;

/** Maximum word count for an insight title. Enforced on publish; shown live in the editor. */
export const INSIGHT_TITLE_MAX_WORDS = 20;

/** Maximum word count for an insight body. Enforced on publish; shown live in the editor. */
export const INSIGHT_BODY_MAX_WORDS = 150;

/** Mutable draft version of an expert insight. */
export interface ExpertInsightDraft {
  id: string;
  title: string;
  body: string;
}

/** The single current (most recently published) immutable version. */
export interface ExpertInsightCurrentVersion {
  id: string;
  title: string;
  body: string;
  publishedAt: string;
}

/** Full insight record as returned by the contributor-facing API. */
export interface ExpertInsightSummary {
  id: string;
  status: "active" | "withdrawn";
  createdAt: string;
  /** Taxonomy field URNs this insight is tagged with. Required before publishing. */
  fields: string[];
  /** Null when no unpublished draft exists (i.e. most recent draft was published). */
  draft: ExpertInsightDraft | null;
  /** Null when the insight has never been published. */
  currentVersion: ExpertInsightCurrentVersion | null;
}

/** Request body for creating a new insight (with its initial draft). */
export interface CreateInsightBody {
  title: string;
  body: string;
  /** Taxonomy field URNs to tag the insight with. Optional on create; required before publish. */
  fields?: string[];
}

/** Request body for updating the draft of an existing insight. */
export interface UpdateDraftBody {
  title: string;
  body: string;
  /** Taxonomy field URNs to tag the insight with. Optional; required before publish. */
  fields?: string[];
}

/** Contributor's own profile — merged view of user_profiles and expert_profiles. */
export interface ContributorProfile {
  userId: string;
  fullName: string;
  avatarUrl: string | null;
  bio: string | null;
  credentials: string | null;
  /** Taxonomy field URNs the contributor is authorized to publish in. Admin-managed. */
  fields: string[];
}

/** Paginated response for the contributor's insight list. */
export type InsightListPage = PageResponse<ExpertInsightSummary>;

/** Fields the contributor may update on their own profile. All optional — only provided fields are written. */
export interface UpdateProfileBody {
  full_name?: string;
  /** Explicitly null to clear. */
  bio?: string | null;
  /** Explicitly null to clear. */
  credentials?: string | null;
}
